import {emptyArray} from '@packages/utilities/fp/utils';

import {normalizeValue} from '@packages/shared/LegacyStyleSystem/utils/StyleStoreUtils';

import * as StringUtils from '@packages/utilities/string-utils';
import {generateGuid} from '@packages/utilities/guid';
import * as BgImageParser from '@packages/shared/LegacyStyleSystem/parsers/BgImageParser';
import {type FontAxesMap} from '@packages/systems/core/constants/Fonts';
import type {FontVariation} from '@packages/systems/style-legacy/values';
import {getFontVariationSettingType} from '../../stores/StyleSystem/typography-connect';
import * as StyleStoreParser from '@packages/shared/LegacyStyleSystem/utils/StyleStoreParser';

const EXAMPLE_IMAGE_REFERENCE = '@img_example_bg';

const EXAMPLE_IMAGE = {
  local: {
    image: {value: EXAMPLE_IMAGE_REFERENCE},
    size: {value: '125px'},
  },
} as const;

// Part makers

export function makeBackgroundImagePart(): any {
  const parts = BgImageParser.parseParts(EXAMPLE_IMAGE);
  // @ts-expect-error - TS2339 - Property 'local' does not exist on type 'Object'.
  const component = parts.local[0].local;
  return {
    _type: 'background',
    _guid: generateGuid(),
    image: normalizeValue({
      local: {value: BgImageParser.FILE_PREFIX + component.imageInfo._id},
    }),
    sourceWidth: normalizeValue({
      local: {value: component.imageInfo.width},
    }),
    sourceHeight: normalizeValue({
      local: {value: component.imageInfo.height},
    }),
    sourceSize: normalizeValue({
      local: {value: StringUtils.prettySize(component.imageInfo.fileSize)},
    }),
    width: normalizeValue({
      local: {value: 'auto'},
      placeholder: {value: 'Auto'},
    }),
    height: normalizeValue({
      local: {value: null},
      placeholder: {value: 'Auto'},
    }),
    attachment: normalizeValue({
      local: {value: null},
      browser: {value: 'scroll', unit: null},
    }),
    fileName: normalizeValue({
      local: {value: component.imageInfo.fileName},
    }),
    origFileName: normalizeValue({
      local: {value: component.imageInfo.origFileName},
    }),
    top: normalizeValue({local: {value: 0, unit: 'px'}}),
    left: normalizeValue({local: {value: 0, unit: 'px'}}),
    repeat: normalizeValue({
      local: {value: null},
      browser: {value: 'repeat', unit: null},
    }),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export function makeBackgroundSolidColorPart(): any {
  return {
    _type: 'solidcolor',
    _guid: generateGuid(),
    image: normalizeValue({
      local: {
        value: `linear-gradient(${StyleStoreParser.HACK_SOLID_COLOR_VALUE}, rgba(0,0,0,0.50), rgba(0,0,0,0.50))`,
      },
    }),
    color: normalizeValue({local: {value: 'rgba(0,0,0,0.50)'}}),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export function makeBackgroundGradientPart(): any {
  return {
    _type: 'gradient',
    _guid: generateGuid(),
    image: normalizeValue({
      local: {value: 'linear-gradient(180deg, black, white)'},
    }),
    type: normalizeValue({local: {value: 'linear'}}),
    angle: normalizeValue({local: {value: 180, unit: 'deg'}}),
    size: normalizeValue({local: {value: 'farthest-corner'}}),
    stops: normalizeValue({
      local: {
        value: [
          {
            position: normalizeValue({local: {value: 0, unit: '%'}}),
            color: normalizeValue({local: {value: 'black'}}),
            _guid: generateGuid(),
          },
          {
            position: normalizeValue({local: {value: 100, unit: '%'}}),
            color: normalizeValue({local: {value: 'white'}}),
            _guid: generateGuid(),
          },
        ],
      },
    }),
    repeat: normalizeValue({local: {value: 'no-repeat'}}),
    position: normalizeValue({local: {value: null}}),
    attachment: normalizeValue({local: {value: null}}),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export function makeBackgroundRadialGradientPart(): any {
  return {
    _type: 'gradient',
    _guid: generateGuid(),
    image: normalizeValue({
      local: {value: 'linear-gradient(180deg, black, white)'},
    }),
    type: normalizeValue({local: {value: 'radial'}}),
    angle: normalizeValue({local: {value: 180, unit: 'deg'}}),
    size: normalizeValue({local: {value: 'farthest-corner'}}),
    stops: normalizeValue({
      local: {
        value: [
          {
            position: normalizeValue({local: {value: 0, unit: '%'}}),
            color: normalizeValue({local: {value: 'black'}}),
            _guid: generateGuid(),
          },
          {
            position: normalizeValue({local: {value: 100, unit: '%'}}),
            color: normalizeValue({local: {value: 'white'}}),
            _guid: generateGuid(),
          },
        ],
      },
    }),
    repeat: normalizeValue({local: {value: 'no-repeat'}}),
    position: normalizeValue({local: {value: '50% 50%'}}),
    attachment: normalizeValue({local: {value: null}}),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export const makeShadowBoxHighlightPart = _shadowPartMaker(
  'box',
  180,
  1,
  0,
  0,
  'rgba(0,0,0,0.5)',
  true
);
export const makeShadowBoxInnerShadowPart = _shadowPartMaker(
  'box',
  0,
  0,
  8,
  0,
  'black',
  true
);
export const makeShadowBoxOuterGlowPart = _shadowPartMaker(
  'box',
  0,
  0,
  6,
  0,
  'white',
  false
);
export const makeShadowBoxDropShadowPart = _shadowPartMaker(
  'box',
  0,
  2,
  5,
  0,
  'rgba(0,0,0,0.2)',
  false
);
export const makeShadowTextDropShadowPart = _shadowPartMaker(
  'text',
  0,
  1,
  1,
  null,
  'rgba(0,0,0,0.2)'
);
export const makeShadowTextLetterpressShadowPart = _shadowPartMaker(
  'text',
  180,
  1,
  0,
  null,
  'black',
  false
);
export const makeShadowTextLetterpressHighlightPart = _shadowPartMaker(
  'text',
  0,
  1,
  0,
  null,
  'rgba(255,255,255,0.25)',
  false
);

export const makeFilterBlurPart = _filterPartMaker(
  5,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null
);

export function makeTransitionPart(): any {
  return {
    _type: 'transition',
    _guid: generateGuid(),
    property: normalizeValue({local: {value: 'opacity', unit: null}}),
    duration: normalizeValue({local: {value: 200, unit: 'ms'}}),
    delay: normalizeValue({local: {value: 0, unit: 'ms'}}),
    timing: normalizeValue({local: {value: 'ease', unit: null}}),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export function makeTransformScalePart(): any {
  return {
    _type: 'transform',
    _guid: generateGuid(),
    rotateX: normalizeValue({local: {value: null, unit: null}}),
    rotateY: normalizeValue({local: {value: null, unit: null}}),
    rotateZ: normalizeValue({local: {value: null, unit: null}}),
    translateX: normalizeValue({local: {value: null}}),
    translateY: normalizeValue({local: {value: null}}),
    translateZ: normalizeValue({local: {value: null}}),
    scaleX: normalizeValue({local: {value: 1}}),
    scaleY: normalizeValue({local: {value: 1}}),
    scaleZ: normalizeValue({local: {value: 1}}),
    skewX: normalizeValue({local: {value: null}}),
    skewY: normalizeValue({local: {value: null}}),
    perspective: normalizeValue({local: {value: null}}),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export function makeTransformTranslatePart(): any {
  return {
    _type: 'transform',
    _guid: generateGuid(),
    rotateX: normalizeValue({local: {value: null, unit: null}}),
    rotateY: normalizeValue({local: {value: null, unit: null}}),
    rotateZ: normalizeValue({local: {value: null, unit: null}}),
    translateX: normalizeValue({local: {value: 0, unit: 'px'}}),
    translateY: normalizeValue({local: {value: 0, unit: 'px'}}),
    translateZ: normalizeValue({local: {value: 0, unit: 'px'}}),
    scaleX: normalizeValue({local: {value: null}}),
    scaleY: normalizeValue({local: {value: null}}),
    scaleZ: normalizeValue({local: {value: null}}),
    skewX: normalizeValue({local: {value: null}}),
    skewY: normalizeValue({local: {value: null}}),
    perspective: normalizeValue({local: {value: null}}),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export function makeTransformRotatePart(): any {
  return {
    _type: 'transform',
    _guid: generateGuid(),
    rotateX: normalizeValue({local: {value: 0, unit: 'deg'}}),
    rotateY: normalizeValue({local: {value: 0, unit: 'deg'}}),
    rotateZ: normalizeValue({local: {value: 0, unit: 'deg'}}),
    translateX: normalizeValue({local: {value: null}}),
    translateY: normalizeValue({local: {value: null}}),
    translateZ: normalizeValue({local: {value: null}}),
    scaleX: normalizeValue({local: {value: null}}),
    scaleY: normalizeValue({local: {value: null}}),
    scaleZ: normalizeValue({local: {value: null}}),
    skewX: normalizeValue({local: {value: null}}),
    skewY: normalizeValue({local: {value: null}}),
    perspective: normalizeValue({local: {value: null}}),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export function makeTransformSkewPart(): any {
  return {
    _type: 'transform',
    _guid: generateGuid(),
    rotateX: normalizeValue({local: {value: null, unit: null}}),
    rotateY: normalizeValue({local: {value: null, unit: null}}),
    rotateZ: normalizeValue({local: {value: null, unit: null}}),
    translateX: normalizeValue({local: {value: null}}),
    translateY: normalizeValue({local: {value: null}}),
    translateZ: normalizeValue({local: {value: null}}),
    scaleX: normalizeValue({local: {value: null}}),
    scaleY: normalizeValue({local: {value: null}}),
    scaleZ: normalizeValue({local: {value: null}}),
    skewX: normalizeValue({local: {value: 0, unit: 'deg'}}),
    skewY: normalizeValue({local: {value: 0, unit: 'deg'}}),
    perspective: normalizeValue({local: {value: null}}),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export function makeTransformPerspectivePart(): any {
  return {
    _type: 'transform',
    _guid: generateGuid(),
    rotateX: normalizeValue({local: {value: null, unit: null}}),
    rotateY: normalizeValue({local: {value: null, unit: null}}),
    rotateZ: normalizeValue({local: {value: null, unit: null}}),
    translateX: normalizeValue({local: {value: null}}),
    translateY: normalizeValue({local: {value: null}}),
    translateZ: normalizeValue({local: {value: null}}),
    scaleX: normalizeValue({local: {value: null}}),
    scaleY: normalizeValue({local: {value: null}}),
    scaleZ: normalizeValue({local: {value: null}}),
    skewX: normalizeValue({local: {value: null}}),
    skewY: normalizeValue({local: {value: null}}),
    perspective: normalizeValue({local: {value: 1000, unit: 'px'}}),
    hidden: normalizeValue({local: {value: false}}),
  };
}

export function makeFontVariationSettingPart(
  fontVariableAxes: FontAxesMap,
  propertyList: Array<FontVariation> | null | undefined,
  axisValue: {
    tag: string;
    value: number;
  }
): any {
  const {tag: axisTag, value} = axisValue;

  const alreadySet = (propertyList ?? emptyArray).reduce((set, setting) => {
    set.add(getFontVariationSettingType(setting));
    return set;
  }, new Set());

  if (alreadySet.has(axisTag)) {
    return null;
  }

  const rest: Record<string, any> = {};

  for (const tag in fontVariableAxes) {
    const axis = fontVariableAxes[tag];
    // @ts-expect-error - TS18048 - 'axis' is possibly 'undefined'.
    rest[axis.tag] = normalizeValue({local: {value: null, unit: null}});
  }

  const axisToSet = {
    tag: axisTag,
    value: normalizeValue({
      local: {value, unit: null},
    }),
  } as const;

  return {
    _type: 'fontVariationSettings',
    _guid: generateGuid(),
    ...rest,
    [axisToSet.tag]: axisToSet.value,
    hidden: normalizeValue({local: {value: false}}),
  };
}
function _shadowPartMaker(
  target: 'box' | 'text',
  x: number,
  y: number,
  blur: number,
  spread: number | null | undefined,
  color: string,
  inset?: boolean
): any {
  return function () {
    return {
      _type: 'shadow',
      _guid: generateGuid(),
      _target: target,
      position: normalizeValue({
        local: {value: !inset ? 'outside' : 'inside'},
      }),
      x: normalizeValue({local: {value: x, unit: 'px'}}),
      y: normalizeValue({local: {value: y, unit: 'px'}}),
      blur: normalizeValue({local: {value: blur, unit: 'px'}}),
      spread: normalizeValue({
        local: {value: spread || 0, unit: 'px'},
      }),
      color: normalizeValue({local: {value: color}}),
      hidden: normalizeValue({local: {value: false}}),
    };
  };
}

function _filterPartMaker(
  blur: number,
  dropShadow: null,
  hueRotate: null,
  saturate: null,
  brightness: null,
  contrast: null,
  grayscale: null,
  sepia: null,
  invert: null
): any {
  return function () {
    return {
      _type: 'filter',
      _guid: generateGuid(),
      blur: normalizeValue({local: {value: blur, unit: 'px'}}),
      dropShadow: normalizeValue({local: {value: dropShadow, unit: null}}),
      hueRotate: normalizeValue({
        local: {value: hueRotate, unit: 'deg'},
      }),
      saturate: normalizeValue({local: {value: saturate, unit: '%'}}),
      brightness: normalizeValue({
        local: {value: brightness, unit: '%'},
      }),
      contrast: normalizeValue({local: {value: contrast, unit: '%'}}),
      grayscale: normalizeValue({
        local: {value: grayscale, unit: '%'},
      }),
      sepia: normalizeValue({local: {value: sepia, unit: '%'}}),
      invert: normalizeValue({local: {value: invert, unit: '%'}}),
      hidden: normalizeValue({local: {value: false}}),
    };
  };
}
